import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Table,
  Tag,
  Tooltip,
  Spin,
  Empty,
  Alert,
} from '@surecompanies/core_components'
import { message, Modal } from 'antd'
import { CopyOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { copyToClipboard } from 'components/CopyLink/CopyText'
import type { ColumnsType } from 'antd/es/table'
import type { ICellRendererParams } from '@ag-grid-community/core'

import type { EmployeeVcnCard } from '../../api/dto/employee-vcn-cards.dto'
import {
  createNewVcnSelected,
  terminateAndNewVcnSelected,
  terminateEmployeeVcnCards,
} from '../../api/vcn-management-api-client'
import type { VcnSelectionResponse } from '../../api/dto/vcn-selection.dto'
import type { VcnManagementBenefitRow } from './types'
import { VcnCardStatus } from './types'
import { VcnProviderRadio } from './VcnProviderRadio'
import { VirtualCardType } from 'modules/virtual-cards/constants'
import { useVcnProviderAvailability } from 'modules/virtual-cards/useVcnProviderAvailability'
import {
  EMPLOYEE_VCN_CARDS_QUERY_KEY,
  useEmployeeVcnCards,
} from './hooks/useEmployeeVcnCards'
import { useInvalidateVcnData } from './hooks/useInvalidateVcnData'
import type { StatusTone } from './vcnCardStatus'
import {
  getStatusPillStyle,
  getVcnCardStatusLabel,
  getVcnCardStatusTone,
} from './vcnCardStatus'
import {
  getVcnProviderTagColor,
  getVcnProviderTagStyle,
} from './VcnManagementTableCellTags'
import {
  showCardActionOutcome,
  type CardActionOutcome,
} from './cardActionOutcome'
import CardActionBar, {
  CardActionKind,
  CARD_ACTION_LABELS,
} from './CardActionBar'

const MUTED = '#8c8c8c'

const cardKey = (card: EmployeeVcnCard): string =>
  `${card.provider}:${card.virtualCardKey}`

const cardLabel = (
  card: EmployeeVcnCard | undefined,
  fallback: string
): string => (card ? `${card.vcnNumber} · ${card.memberName}` : fallback)

/** Create / terminate-and-new require an active benefit binding (EPS also enforces). */
const isBoundToActiveBenefit = (card: EmployeeVcnCard): boolean =>
  card.inUse && Boolean(card.benefitId)

function BenefitIdWithCopy({ benefitId }: { benefitId: string }) {
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    void copyToClipboard(benefitId).then((ok) => {
      if (ok) void message.success('Benefit ID copied')
    })
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontFamily: 'monospace' }}>{benefitId}</span>
      <CopyOutlined
        onClick={handleCopy}
        style={{ cursor: 'pointer' }}
        aria-label="Copy benefit ID"
      />
    </span>
  )
}

// Interactive tooltip (Tooltip mouseLeaveDelay keeps it open) so the benefit id
// stays reachable to click-copy.
function InUseTooltipContent({ card }: { card: EmployeeVcnCard }) {
  if (card.inUse) {
    return card.benefitId ? (
      <span>
        On active benefit <BenefitIdWithCopy benefitId={card.benefitId} />
      </span>
    ) : (
      <span>On an active benefit</span>
    )
  }
  if (card.benefitId) {
    return (
      <span>
        Last on benefit <BenefitIdWithCopy benefitId={card.benefitId} /> (ended)
      </span>
    )
  }
  return <span>Not on any benefit</span>
}

// Three states per the EMS-8827 designer redesign, worded as the PM asked at
// the 2026-09-15 demo: on an active benefit, on one that has ended, or never
// bound. Kept in step with VCN_CARD_IN_USE_LABELS — the same column appears in
// Card Mode, and two spellings of one state is how a grid loses trust.
function inUseDisplay(card: EmployeeVcnCard): {
  label: string
  tone: StatusTone
} {
  if (card.inUse) {
    return { label: 'On Active Plan', tone: 'success' }
  }
  if (card.benefitId) {
    return { label: 'On Ended Plan', tone: 'pending' }
  }
  return { label: 'No Plan', tone: 'error' }
}

const columns: ColumnsType<EmployeeVcnCard> = [
  {
    title: 'VCN number',
    dataIndex: 'vcnNumber',
    key: 'vcnNumber',
    render: (value: string) => (
      <span style={{ fontFamily: 'monospace' }}>{value || '—'}</span>
    ),
  },
  {
    title: 'Member',
    dataIndex: 'memberName',
    key: 'memberName',
    render: (value: string) => value || <span style={{ color: MUTED }}>—</span>,
  },
  {
    title: 'VCN provider',
    dataIndex: 'provider',
    key: 'provider',
    width: 130,
    render: (value: string) => {
      const raw = String(value ?? '').trim()
      if (!raw) return <span style={{ color: MUTED }}>—</span>
      return (
        <Tag
          color={getVcnProviderTagColor(raw)}
          style={getVcnProviderTagStyle(raw)}
        >
          {raw}
        </Tag>
      )
    },
  },
  {
    title: 'Card status',
    dataIndex: 'cardStatus',
    key: 'cardStatus',
    width: 130,
    render: (status: VcnCardStatus) =>
      status ? (
        <span style={getStatusPillStyle(getVcnCardStatusTone(status))}>
          {getVcnCardStatusLabel(status)}
        </span>
      ) : (
        <span style={{ color: MUTED }}>—</span>
      ),
  },
  {
    title: 'Plan',
    dataIndex: 'inUse',
    key: 'inUse',
    width: 100,
    render: (_: boolean, card: EmployeeVcnCard) => {
      const { label, tone } = inUseDisplay(card)
      return (
        <Tooltip
          title={<InUseTooltipContent card={card} />}
          mouseLeaveDelay={0.3}
        >
          <span style={getStatusPillStyle(tone)}>{label}</span>
        </Tooltip>
      )
    },
  },
]

function CardCountsHeader({ cards }: { cards: EmployeeVcnCard[] }) {
  const terminated = cards.filter(
    (c) => c.cardStatus === VcnCardStatus.TERMINATED
  ).length
  const active = cards.filter(
    (c) => c.cardStatus === VcnCardStatus.ACTIVE
  ).length
  return (
    <div style={{ marginBottom: 10, fontSize: 12, color: MUTED }}>
      <span
        style={{
          fontWeight: 600,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
          marginRight: 10,
        }}
      >
        Associated VCNs
      </span>
      {cards.length} {cards.length === 1 ? 'card' : 'cards'} · {terminated}{' '}
      terminated · {active} active
    </div>
  )
}

function mapByBenefitId(
  cards: EmployeeVcnCard[]
): Map<string, EmployeeVcnCard> {
  const byBenefitId = new Map<string, EmployeeVcnCard>()
  cards.forEach((card) => {
    if (card.benefitId) {
      byBenefitId.set(card.benefitId, card)
    }
  })
  return byBenefitId
}

// Normalizes the benefit-keyed grid response into the shared outcome. The
// request dedupes selected cards to distinct benefits, so counts here are
// resolved back to CARDS — one benefit result applies to every selected card on
// that benefit — keeping the success/failure counts consistent with the "N
// card(s)" the confirm dialog showed.
function selectionOutcome(
  result: VcnSelectionResponse,
  cards: EmployeeVcnCard[]
): CardActionOutcome {
  const cardsByBenefitId = new Map<string, EmployeeVcnCard[]>()
  cards.forEach((card) => {
    if (card.benefitId) {
      const list = cardsByBenefitId.get(card.benefitId) ?? []
      list.push(card)
      cardsByBenefitId.set(card.benefitId, list)
    }
  })
  let successCount = 0
  let failedCount = 0
  const failureLines: string[] = []
  result.results.forEach((r) => {
    const affectedCards = cardsByBenefitId.get(r.benefitId) ?? []
    if (r.success) {
      successCount += affectedCards.length
      return
    }
    failedCount += affectedCards.length
    affectedCards.forEach((card) => {
      failureLines.push(
        `${cardLabel(card, r.benefitId)}: ${r.errorMessage ?? 'unknown error'}`
      )
    })
  })
  return { successCount, failedCount, failureLines }
}

/**
 * ag-Grid master-detail cell renderer for a benefit row: the "Associated VCNs"
 * sub-table listing every VCN card for that benefit's employee (both providers),
 * plus the card selection + floating action bar (EMS-8827 Parts C & D).
 */
export default function AssociatedVcnsPanel(
  params: ICellRendererParams<VcnManagementBenefitRow>
) {
  const queryClient = useQueryClient()
  const invalidateVcnData = useInvalidateVcnData()
  const providerAvailability = useVcnProviderAvailability()
  const { hasAnyProvider } = providerAvailability
  const employeePublicKey = params.data?.employeePublicKey
  const { data, isLoading, isFetching, error } = useEmployeeVcnCards(
    employeePublicKey,
    true
  )
  const cards = useMemo(() => data?.cards ?? [], [data])
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [activeAction, setActiveAction] = useState<CardActionKind | null>(null)

  // detailRowAutoHeight measures before async content lays out; defer remeasure
  // until after paint so the Ant Design table has settled (same pattern as other
  // grids in this repo). Avoid ResizeObserver — it fights expand/collapse.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      params.api.resetRowHeights()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [params.api, isLoading, cards.length])

  const cardByKey = useMemo(() => {
    const map = new Map<string, EmployeeVcnCard>()
    cards.forEach((card) => map.set(cardKey(card), card))
    return map
  }, [cards])

  // Drop keys for missing or terminated cards after a refetch so disabled
  // checkboxes cannot remain checked and be posted to card actions.
  useEffect(() => {
    setSelectedKeys((prev) => {
      const stillSelectable = prev.filter((key) => {
        const card = cardByKey.get(key)
        return card != null && card.cardStatus !== VcnCardStatus.TERMINATED
      })
      const isUnchanged =
        stillSelectable.length === prev.length &&
        stillSelectable.every((key, i) => key === prev[i])
      return isUnchanged ? prev : stillSelectable
    })
  }, [cardByKey])

  const selectedCards = useMemo(
    () =>
      selectedKeys.flatMap((key) => {
        const card = cardByKey.get(key)
        return card ? [card] : []
      }),
    [selectedKeys, cardByKey]
  )

  // Availability (server also enforces): create-new / terminate-and-new require
  // every selected card bound to an active benefit; terminate needs at least
  // one not-yet-terminated card (already-terminated cards have nothing to terminate).
  const canCreateOrTerminateAndNew =
    selectedCards.length > 0 && selectedCards.every(isBoundToActiveBenefit)
  const actionableTerminateCards = useMemo(
    () =>
      selectedCards.filter(
        (card) => card.cardStatus !== VcnCardStatus.TERMINATED
      ),
    [selectedCards]
  )
  const canTerminate = actionableTerminateCards.length > 0

  const clearSelection = useCallback(() => setSelectedKeys([]), [])

  // Modal.confirm freezes its onOk closure when it opens, so read live lists
  // through refs: a background refetch that prunes the selection (or flips card
  // statuses) while the dialog is open is then honored on confirm.
  const selectedCardsRef = useRef(selectedCards)
  selectedCardsRef.current = selectedCards
  const actionableTerminateCardsRef = useRef(actionableTerminateCards)
  actionableTerminateCardsRef.current = actionableTerminateCards

  const runCardAction = useCallback(
    async (
      action: CardActionKind,
      execute: (cards: EmployeeVcnCard[]) => Promise<CardActionOutcome>
    ): Promise<void> => {
      const actionLabel = CARD_ACTION_LABELS[action]
      const eligibleCards =
        action === CardActionKind.TERMINATE
          ? actionableTerminateCardsRef.current
          : selectedCardsRef.current.filter(isBoundToActiveBenefit)
      // Reject so Modal.confirm stays open — a silent resolve would close the
      // dialog and look like the destructive action succeeded.
      if (!employeePublicKey || eligibleCards.length === 0) {
        void message.warning(
          `No eligible cards left to ${actionLabel}. Selection may have changed — review and try again.`
        )
        throw new Error('No eligible cards for card action')
      }
      setActiveAction(action)
      try {
        const outcome = await execute(eligibleCards)
        showCardActionOutcome(actionLabel, outcome)
        clearSelection()
        // Do not await invalidation — TanStack Query can leave that promise pending
        // when refetches are paused (e.g. offline), which would stick activeAction
        // and keep the action bar disabled. Selection is already cleared.
        // Terminating a card here changes what BOTH tabs list, so this goes
        // through the shared invalidation rather than naming keys locally —
        // naming them locally is how Card Mode came to be missed.
        invalidateVcnData()
        void queryClient.invalidateQueries({
          queryKey: [EMPLOYEE_VCN_CARDS_QUERY_KEY, employeePublicKey],
        })
      } catch (error) {
        void message.error(`${actionLabel} failed. Please try again.`)
        // Rethrow so Modal.confirm keeps the dialog open on failure.
        throw error
      } finally {
        setActiveAction(null)
      }
    },
    [employeePublicKey, clearSelection, queryClient]
  )

  // Terminate is the only card-keyed endpoint (targets a specific card by token).
  const handleTerminate = useCallback(() => {
    Modal.confirm({
      title: `${CARD_ACTION_LABELS[CardActionKind.TERMINATE]} ${actionableTerminateCards.length} card(s)?`,
      content:
        'This deactivates the selected virtual cards. This cannot be undone.',
      okText: CARD_ACTION_LABELS[CardActionKind.TERMINATE],
      okButtonProps: { danger: true },
      onOk: () =>
        runCardAction(CardActionKind.TERMINATE, async (cards) => {
          const result = await terminateEmployeeVcnCards(
            cards.map((card) => ({
              provider: card.provider,
              virtualCardKey: card.virtualCardKey,
            }))
          )
          const cardByKey = new Map(
            cards.map((card) => [
              `${card.provider}:${card.virtualCardKey}`,
              card,
            ])
          )
          return {
            successCount: result.successCount,
            failedCount: result.failedCount,
            failureLines: result.results
              .filter((r) => !r.success)
              .map(
                (r) =>
                  `${cardLabel(cardByKey.get(`${r.provider}:${r.virtualCardKey}`), r.virtualCardKey)}: ${r.errorMessage ?? 'unknown error'}`
              ),
          }
        }),
    })
  }, [runCardAction, actionableTerminateCards.length])

  // Terminate & New and Create reuse the benefit-keyed grid endpoints — an
  // in-use card IS its benefit's active VCN, so we send the benefit ids.
  const handleTerminateAndNew = useCallback(() => {
    let provider =
      providerAvailability.enabledProviders[0] ?? VirtualCardType.WEX
    Modal.confirm({
      title: `${CARD_ACTION_LABELS[CardActionKind.TERMINATE_AND_NEW]} for ${selectedCards.length} card(s)?`,
      content: (
        <div>
          This deactivates each selected card and issues a new card with the
          chosen provider on its benefit. This cannot be undone.
          <VcnProviderRadio
            availability={providerAvailability}
            onChange={(value) => (provider = value)}
          />
        </div>
      ),
      okText: CARD_ACTION_LABELS[CardActionKind.TERMINATE_AND_NEW],
      // Both providers switched off — no card can be issued (EMS-9720).
      okButtonProps: { danger: true, disabled: !hasAnyProvider },
      onOk: () =>
        runCardAction(CardActionKind.TERMINATE_AND_NEW, async (cards) => {
          const benefitIds = Array.from(mapByBenefitId(cards).keys())
          const result = await terminateAndNewVcnSelected(benefitIds, provider)
          return selectionOutcome(result, cards)
        }),
    })
  }, [
    runCardAction,
    selectedCards.length,
    providerAvailability,
    hasAnyProvider,
  ])

  const handleCreate = useCallback(() => {
    let provider =
      providerAvailability.enabledProviders[0] ?? VirtualCardType.WEX
    Modal.confirm({
      title: `Create a new card for ${selectedCards.length} card(s)?`,
      content: (
        <div>
          This issues a new card with the chosen provider on each selected
          card&apos;s benefit. Existing cards are not terminated.
          <VcnProviderRadio
            availability={providerAvailability}
            onChange={(value) => (provider = value)}
          />
        </div>
      ),
      okText: CARD_ACTION_LABELS[CardActionKind.CREATE],
      okButtonProps: { disabled: !hasAnyProvider },
      onOk: () =>
        runCardAction(CardActionKind.CREATE, async (cards) => {
          const benefitIds = Array.from(mapByBenefitId(cards).keys())
          const result = await createNewVcnSelected(benefitIds, provider)
          return selectionOutcome(result, cards)
        }),
    })
  }, [
    runCardAction,
    selectedCards.length,
    providerAvailability,
    hasAnyProvider,
  ])

  if (!employeePublicKey) {
    return (
      <div style={{ padding: 16 }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Employee key unavailable — cannot load associated VCNs"
        />
      </div>
    )
  }
  if (isLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin />
      </div>
    )
  }
  if (error) {
    return (
      <div style={{ padding: 16 }}>
        <Alert
          type="error"
          showIcon
          message="Could not load associated VCNs"
          description={error instanceof Error ? error.message : undefined}
        />
      </div>
    )
  }
  return (
    <div style={{ padding: '16px 24px 24px' }}>
      <CardCountsHeader cards={cards} />
      <div
        style={{
          background: '#fff',
          border: '1px solid #f0f0f0',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        {/* Post-action refetch keeps showing pre-action rows until it lands;
            spin + disable selection so stale cards can't be re-submitted. */}
        <Spin spinning={isFetching}>
          <Table<EmployeeVcnCard>
            rowKey={cardKey}
            rowSelection={{
              selectedRowKeys: selectedKeys,
              onChange: (keys) => setSelectedKeys(keys.map(String)),
              // Already-terminated cards have nothing to act on — not selectable
              // (antd renders the checkbox disabled with a not-allowed cursor).
              // Also lock selection while a refetch is settling the data.
              getCheckboxProps: (card) => ({
                disabled:
                  isFetching || card.cardStatus === VcnCardStatus.TERMINATED,
              }),
            }}
            columns={columns}
            dataSource={cards}
            pagination={false}
            size="small"
            locale={{ emptyText: 'No VCN cards for this employee' }}
          />
        </Spin>
      </div>
      <CardActionBar
        selectedCount={selectedCards.length}
        activeAction={activeAction}
        // `!isFetching` is the same refetch lock as the checkboxes — don't act
        // on stale cached rows.
        actions={[
          {
            kind: CardActionKind.TERMINATE,
            danger: true,
            disabled: !canTerminate || isFetching,
            onClick: handleTerminate,
          },
          {
            kind: CardActionKind.TERMINATE_AND_NEW,
            disabled: !canCreateOrTerminateAndNew || isFetching,
            onClick: handleTerminateAndNew,
          },
          {
            kind: CardActionKind.CREATE,
            disabled: !canCreateOrTerminateAndNew || isFetching,
            onClick: handleCreate,
          },
        ]}
        onClose={clearSelection}
      />
    </div>
  )
}
